import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, ArrowRight, Copy, Chalkboard, Eye, Heart, TrendUp, Megaphone, X, Fire, Star } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { getMediaUrl } from '../../lib/media';
import CreateClassFilmModal from '../../components/classroom/CreateClassFilmModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const relativeDay = (dayIso) => {
  if (!dayIso) return null;
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const day = new Date(`${dayIso}T00:00:00Z`);
  const diffDays = Math.floor((today.getTime() - day.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Viewed today';
  if (diffDays === 1) return 'Viewed yesterday';
  if (diffDays < 7) return `Viewed ${diffDays} days ago`;
  return `Last viewed ${day.toLocaleDateString()}`;
};

/**
 * Single classroom view — list collections (e.g. "Class A", "Trip to the
 * museum"), create new ones, and surface the parent share link.
 */
const WorkspacePage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [ws, setWs] = useState(null);
  const [collections, setCollections] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [bestMoment, setBestMoment] = useState(null);
  const [mostShared, setMostShared] = useState(null);
  const [highlightsReel, setHighlightsReel] = useState(null);
  const [highlightsSending, setHighlightsSending] = useState(false);
  const [printCheckoutLoading, setPrintCheckoutLoading] = useState(false);
  const [filmOpen, setFilmOpen] = useState(false);
  const [latestFilm, setLatestFilm] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [streakDays, setStreakDays] = useState(0);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const sendHighlightsReel = async () => {
    if (highlightsSending) return;
    setHighlightsSending(true);
    try {
      const res = await axios.post(`${API}/workspaces/${workspaceId}/highlights-reel/send`);
      const { sent = 0, recipients = 0 } = res.data || {};
      toast.success(
        recipients > 0
          ? `✨ Sent to ${sent} ${sent === 1 ? 'family' : 'families'}`
          : 'Reel sent.',
        { duration: 5000 },
      );
      // Refresh the reel state to surface the "sent" timestamp
      const refreshed = await axios.get(`${API}/workspaces/${workspaceId}/highlights-reel`);
      setHighlightsReel(refreshed.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send the reel');
    } finally {
      setHighlightsSending(false);
    }
  };

  const startPrintCheckout = async () => {
    if (printCheckoutLoading) return;
    setPrintCheckoutLoading(true);
    try {
      const res = await axios.post(
        `${API}/workspaces/${workspaceId}/highlights-reel/print-checkout`,
      );
      const url = res.data?.url;
      if (!url) throw new Error('No checkout URL returned');
      // Same tab — Stripe sends them back to /classroom/{wid}?print_session_id=…
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start checkout');
      setPrintCheckoutLoading(false);
    }
  };

  const fetchNudge = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/workspaces/${workspaceId}/nudge`);
      setNudge(r.data?.nudge || null);
    } catch {
      setNudge(null);
    }
  }, [workspaceId]);

  // Retry helper for transient failures (cold-start timeouts, mobile
  // network blips, brief auth-refresh races). 4xx fails fast.
  const withRetry = useCallback(async (fn, attempts = 3) => {
    let lastErr;
    for (let i = 0; i < attempts; i += 1) {
      try { return await fn(); }
      catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        const isTransient = !status || status >= 500;
        if (!isTransient || i === attempts - 1) throw err;
        await new Promise((r) => setTimeout(r, 400 * (i + 1) ** 2));
      }
    }
    throw lastErr;
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Teachers came in via magic link — keep them in the passwordless
      // flow rather than dumping them on the email/password /login page.
      navigate(`/teachers/start?redirect=/classroom/${workspaceId}`);
      return;
    }
    (async () => {
      try {
        const [w, c, e, s, b, m, h] = await withRetry(() => Promise.all([
          axios.get(`${API}/workspaces/${workspaceId}`),
          axios.get(`${API}/workspaces/${workspaceId}/collections`),
          axios.get(`${API}/workspaces/${workspaceId}/engagement`),
          axios.get(`${API}/workspaces/${workspaceId}/streak`),
          axios.get(`${API}/workspaces/${workspaceId}/best-moment`),
          axios.get(`${API}/workspaces/${workspaceId}/most-shared`),
          axios.get(`${API}/workspaces/${workspaceId}/highlights-reel`),
        ]));
        setWs(w.data);
        setCollections(c.data?.items || []);
        setEngagement(e.data || null);
        setStreakDays(s.data?.streak_days || 0);
        setBestMoment(b.data || null);
        setMostShared(m.data || null);
        setHighlightsReel(h.data || null);
        await fetchNudge();
        // One-time celebration toast — fires only on the first workspace
        // load where forwards have crossed the milestone (≥5). Per-device,
        // per-workspace; once shown, never shown again on this device. We
        // delay by 800ms so it lands AFTER the engagement strip has rendered
        // (per the brief: "fire on workspace load, after engagement strip").
        const forwards = e.data?.forward_views_7d || 0;
        if (forwards >= 5 && typeof window !== 'undefined') {
          const seenKey = `share_milestone_seen_${workspaceId}`;
          if (!localStorage.getItem(seenKey)) {
            setTimeout(() => {
              toast.success(
                `Wow — your photos travelled to ${forwards} families this week 🎉`,
                { duration: 5000 },
              );
              try { localStorage.setItem(seenKey, '1'); } catch { /* quota / private mode */ }
            }, 800);
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not load classroom');
        navigate('/classroom');
      } finally {
        setLoading(false);
      }
    })();
  }, [workspaceId, user, authLoading, navigate, fetchNudge]);

  const dismissNudge = async () => {
    if (!nudge) return;
    const dismissed = nudge.collection_id;
    setNudge(null);
    try {
      await axios.post(`${API}/workspaces/${workspaceId}/nudge/dismiss`, {
        collection_id: dismissed,
      });
    } catch {
      // optimistic dismiss; ignore failure
    }
  };

  const actOnNudge = () => {
    if (!nudge) return;
    navigate(`/classroom/${workspaceId}/c/${nudge.collection_id}?upload=1`);
  };

  const create = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/workspaces/${workspaceId}/collections`, {
        name: name.trim(),
      });
      setCollections((cs) => [res.data, ...cs]);
      setName('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add collection');
    } finally {
      setCreating(false);
    }
  };

  const shareUrl = ws ? `${window.location.origin}/c/${ws.share_code}` : '';

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Parent link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  // Term-end nudge deep link: when the teacher arrives via the email
  // (`?highlight_reel=true`), gently scroll the reel card into view and
  // pulse the Send button once. Pure attention-direction — never auto-clicks.
  useEffect(() => {
    if (loading || !highlightsReel?.ready) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('highlight_reel') !== 'true') return;
    const t = setTimeout(() => {
      const card = document.querySelector('[data-testid="highlights-reel-card"]');
      if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const sendBtn = document.querySelector('[data-testid="highlights-reel-send"]');
      if (sendBtn) {
        sendBtn.classList.add('reel-pulse');
        setTimeout(() => sendBtn.classList.remove('reel-pulse'), 4500);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [loading, highlightsReel]);

  // Print-checkout return — when teacher comes back from Stripe with
  // `?print_success=true&print_session_id=…`, poll the existing payment
  // status verifier to confirm the order, then celebrate. The verifier
  // promotes the paid transaction into a `classroom_print_orders` row
  // server-side, so no further client work is needed.
  // Fetch the latest ready Class Film — surfaces the "Play in Assembly Mode"
  // button when one exists. Re-fetched whenever the film modal closes (in
  // case the teacher just created one).
  const fetchLatestFilm = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/workspaces/${workspaceId}/films/latest`,
        { validateStatus: (s) => s === 200 || s === 204 || s === 404 },
      );
      setLatestFilm(r.status === 200 ? r.data : null);
    } catch { /* gentle — no toast, no log */ }
  }, [workspaceId]);

  useEffect(() => {
    if (workspaceId) fetchLatestFilm();
  }, [workspaceId, fetchLatestFilm]);

  useEffect(() => {
    if (loading) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('print_session_id');
    if (params.get('print_success') !== 'true' || !sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        // Poll up to 5 times (3s gap) — Stripe's status flips to "paid"
        // shortly after the redirect, but never instantly.
        for (let i = 0; i < 5; i += 1) {
          if (cancelled) return;
          const r = await axios.get(`${API}/checkout/status/${sessionId}`);
          if (r.data?.payment_status === 'paid') {
            toast.success(
              "Your memory book is on its way 📦  We'll email you when it ships.",
              { duration: 7000 },
            );
            // Strip the query so a refresh doesn't re-fire the toast
            const url = new URL(window.location.href);
            url.searchParams.delete('print_session_id');
            url.searchParams.delete('print_success');
            window.history.replaceState({}, '', url.pathname + (url.search || ''));
            return;
          }
          await new Promise((res) => setTimeout(res, 3000));
        }
      } catch {
        // verifier already toasts the user-visible error elsewhere; here
        // we just leave the params in place so a manual refresh can retry
      }
    })();
    return () => { cancelled = true; };
  }, [loading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1014] text-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#B89762]/40 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="workspace-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate('/classroom')}
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-6 transition-colors"
          data-testid="workspace-back"
        >
          <ArrowLeft size={14} weight="bold" /> All classrooms
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          >
            <Chalkboard size={16} weight="fill" className="text-white" />
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#C29D52]">Classroom</span>
        </div>
        <h1 className="text-4xl font-black tracking-tight mb-4">{ws?.name}</h1>

        {/* Class Film onboarding empty state — the magical first touch for
            a brand-new teacher. Shown only when there are no collections yet
            (the moment BEFORE any upload). Disappears the moment they create
            their first collection. No multi-step flow, no tutorial — just
            "upload a few photos and we'll create something beautiful".
            Everything else on this page is hidden behind the Create button
            below so the teacher isn't distracted. */}
        {collections.length === 0 && !latestFilm && (
          <div
            data-testid="class-film-empty-state"
            style={{
              marginTop: '8px',
              marginBottom: '24px',
              padding: '36px 28px 32px',
              background: 'linear-gradient(180deg, rgba(184,148,68,0.10) 0%, rgba(194,157,82,0.04) 100%)',
              border: '1px solid rgba(184,148,68,0.22)',
              borderRadius: '18px',
              color: '#FFFFFF',
              textAlign: 'center',
            }}
          >
            <div style={{
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontSize: 11,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: '#D4AF37',
              paddingBottom: 14,
            }}>✨ Class Film</div>
            <h2 style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 28,
              lineHeight: 1.2,
              fontWeight: 400,
              color: '#FFFFFF',
              margin: 0,
              paddingBottom: 12,
              letterSpacing: '-0.01em',
            }}>
              Turn your class into a film
            </h2>
            <p style={{
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontSize: 14,
              lineHeight: 1.55,
              color: 'rgba(255,255,255,0.72)',
              maxWidth: 420,
              margin: '0 auto',
              paddingBottom: 20,
            }}>
              Upload a few photos and we’ll create something beautiful for you.
            </p>
            <button
              data-testid="class-film-empty-upload-cta"
              onClick={() => {
                const input = document.querySelector('[data-testid="collection-new-name"]');
                if (input) {
                  const nameToFocus = input;
                  nameToFocus.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  setTimeout(() => nameToFocus.focus(), 400);
                }
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '14px 26px',
                background: '#FFFFFF', color: '#0F0F0F',
                border: 'none', borderRadius: 999,
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: 14, fontWeight: 700, letterSpacing: '0.04em',
                cursor: 'pointer',
              }}
            >
              Upload photos →
            </button>
          </div>
        )}

        {/* Engagement strip — momentum signal, not a dashboard. Hidden until
            we have at least one view OR forward to celebrate; otherwise we'd
            nag the teacher with a "0 views" line on day one. */}
        {engagement && (engagement.weekly_views > 0 || (engagement.forward_views_7d || 0) > 0) && (
          <EngagementStrip engagement={engagement} streakDays={streakDays} />
        )}
        {engagement && engagement.weekly_views === 0 && (engagement.forward_views_7d || 0) === 0 && collections.length > 0 && (
          <div
            className="rounded-xl px-4 py-3 mb-6 text-xs text-gray-400"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px dashed rgba(255,255,255,0.08)',
            }}
            data-testid="engagement-empty"
          >
            No views yet — parents will see your updates as soon as they open the share link.
          </div>
        )}

        {/* Best moment this week — single spotlight card, not a stat. Sits
            directly under the engagement strip, only renders when we have a
            real moment to celebrate. */}
        {bestMoment?.moment && (
          <BestMomentCard
            moment={bestMoment.moment}
            forwardCount={bestMoment.forward_count_7d || 0}
            shareCode={bestMoment.share_code}
          />
        )}

        {/* Most shared moment ever — lifetime recognition card. Appears
            below Best Moment so the surface progresses from weekly → lifetime.
            Server-side floor: only rendered when ≥3 forwards on the top
            upload, so it never feels hollow. */}
        {mostShared?.moment && (
          <MostSharedMomentCard
            moment={mostShared.moment}
            forwardCount={mostShared.forward_count || 0}
            shareCode={mostShared.share_code}
          />
        )}

        {/* Discoverable "Create class film" CTA — fills the gap between
            the empty-state hero (collections.length === 0) and the
            HighlightsReelCard surface (≥20 uploads). Without this a teacher
            with 1-19 uploads and no film has nowhere to find the film
            creation flow. The modal itself handles the <4 picks case
            gracefully with a friendly inline error. */}
        {collections.length > 0 && !latestFilm && !(highlightsReel?.ready && highlightsReel.picks?.length > 0) && (
          <div
            data-testid="class-film-cta-card"
            className="rounded-2xl p-5 sm:p-6 mb-6"
            style={{
              background: 'linear-gradient(180deg, rgba(184,148,68,0.10) 0%, rgba(194,157,82,0.04) 100%)',
              border: '1px solid rgba(184,148,68,0.22)',
            }}
          >
            <p
              style={{
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: 11, letterSpacing: '0.22em',
                color: '#D4AF37', textTransform: 'uppercase',
                margin: 0, marginBottom: 10,
              }}
            >
              ✨ Class Film
            </p>
            <h3
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 22, fontWeight: 400, letterSpacing: '-0.01em',
                color: '#FFFFFF', margin: 0, marginBottom: 8, lineHeight: 1.25,
              }}
            >
              Turn your class photos into a short film
            </h3>
            <p
              style={{
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: 13, color: 'rgba(255,255,255,0.65)',
                margin: 0, marginBottom: 18, lineHeight: 1.5, maxWidth: 460,
              }}
            >
              Pick a few moments and we'll turn them into something beautiful — ready for assembly tomorrow.
            </p>
            <button
              type="button"
              data-testid="class-film-create-cta"
              onClick={() => setFilmOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 22px',
                background: '#FFFFFF', color: '#0F0F0F',
                border: 'none', borderRadius: 999,
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: 14, fontWeight: 700, letterSpacing: '0.04em',
                cursor: 'pointer',
              }}
            >
              🎬 Create class film
            </button>
          </div>
        )}

        {/* End-of-term highlights reel — macro artefact. Only renders when
            the workspace has earned the surface (≥20 uploads OR ≥10 lifetime
            forwards). Click reveals a curated preview + Send to families. */}
        {highlightsReel?.ready && highlightsReel.picks?.length > 0 && (
          <HighlightsReelCard
            picks={highlightsReel.picks}
            workspaceName={highlightsReel.workspace_name}
            lastSentAt={highlightsReel.last_sent_at}
            sending={highlightsSending}
            onSend={sendHighlightsReel}
            printLoading={printCheckoutLoading}
            onPrint={highlightsReel.print_enabled ? startPrintCheckout : null}
            onCreateFilm={() => setFilmOpen(true)}
          />
        )}

        <CreateClassFilmModal
          open={filmOpen}
          onClose={() => { setFilmOpen(false); fetchLatestFilm(); }}
          workspaceId={workspaceId}
          picks={highlightsReel?.picks || []}
          workspaceName={highlightsReel?.workspace_name || ws?.name}
        />

        {latestFilm?.film_id && (
          <div
            data-testid="assembly-entry"
            style={{
              marginTop: '24px',
              padding: '20px 22px',
              background: '#0F0F0F',
              color: '#FFFFFF',
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ minWidth: 0, flex: '1 1 220px' }}>
              <div style={{
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: 11,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#B6962B',
                paddingBottom: 6,
              }}>✨ Your class film is ready</div>
              <div style={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 20, fontWeight: 400, lineHeight: 1.25,
              }}>
                {latestFilm.title || 'Your class film'}
              </div>
              {latestFilm.subtitle && (
                <div style={{
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  fontSize: 12, color: '#9A8B6B', paddingTop: 4,
                }}>{latestFilm.subtitle}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a
                href={`/assembly/${workspaceId}/${latestFilm.film_id}`}
                data-testid="assembly-entry-cta"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '14px 22px',
                  background: '#FFFFFF', color: '#0F0F0F',
                  textDecoration: 'none', borderRadius: 999,
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  fontSize: 14, fontWeight: 600, letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}
              >▶ Play in Assembly Mode</a>
              <a
                href={(() => {
                  // Share the OG-rendered URL, not the raw video — so
                  // WhatsApp / iMessage can build a preview card with the
                  // film's title + thumbnail. Falls back to the raw video
                  // URL only if the film_id is somehow missing.
                  const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
                  const shareLink = latestFilm.film_id
                    ? `${backend}/api/film/c/${latestFilm.film_id}`
                    : (latestFilm.video_url || '');
                  const msg = [
                    'Hi! We got sent this from school today ❤️',
                    '',
                    'It’s a short film of the class — thought you’d love it:',
                    '',
                    shareLink,
                  ].join('\n');
                  return `https://wa.me/?text=${encodeURIComponent(msg)}`;
                })()}
                target="_blank" rel="noreferrer"
                data-testid="assembly-entry-share"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '14px 22px',
                  background: 'transparent', color: '#FFFFFF',
                  border: '1px solid rgba(255,255,255,0.25)',
                  textDecoration: 'none', borderRadius: 999,
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  fontSize: 14, fontWeight: 600, letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                }}
              >📤 Share with parents</a>
            </div>
          </div>
        )}

        {/* Smart nudge — at most one positive, action-oriented suggestion */}
        {nudge && (
          <NudgeBanner nudge={nudge} onAct={actOnNudge} onDismiss={dismissNudge} />
        )}

        {/* Share card */}
        <div
          className="rounded-2xl p-4 mb-8 flex items-center gap-3"
          style={{
            background: 'rgba(194,157,82,0.05)',
            border: '1px solid rgba(194,157,82,0.2)',
          }}
        >
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#C29D52] mb-1">
              Parent link
            </div>
            <div className="text-xs font-mono text-gray-300 truncate" data-testid="workspace-share-url">
              {shareUrl}
            </div>
          </div>
          <button
            onClick={copyShare}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold border border-white/15 hover:bg-white/5 transition-all"
            data-testid="workspace-copy-share"
          >
            <Copy size={12} weight="bold" /> Copy
          </button>
        </div>

        {/* Create collection */}
        <form
          onSubmit={create}
          className="rounded-2xl p-5 mb-6"
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px dashed rgba(255,255,255,0.12)',
          }}
        >
          <label className="block text-xs uppercase tracking-[0.18em] font-bold text-gray-400 mb-2">
            New collection
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. "Year 6 — Class A" or "Sports Day"'
              className="flex-1 bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B89762]/40 transition-colors"
              data-testid="collection-new-name"
            />
            <button
              type="submit"
              disabled={creating || !name.trim()}
              className="px-5 py-3 rounded-xl text-sm font-bold border border-white/15 hover:bg-white/5 transition-all disabled:opacity-50"
              data-testid="collection-create"
            >
              <Plus size={14} weight="bold" className="inline mr-1" />
              Add
            </button>
          </div>
        </form>

        {/* List */}
        {collections.length === 0 ? (
          <div className="text-sm text-gray-500" data-testid="collections-empty">
            No collections yet. Create one above.
          </div>
        ) : (
          <div className="space-y-2">
            {collections.map((c) => {
              const lastViewedLabel = relativeDay(c.last_viewed_day);
              return (
                <button
                  key={c.collection_id}
                  onClick={() => navigate(`/classroom/${workspaceId}/c/${c.collection_id}`)}
                  className="w-full text-left rounded-xl px-5 py-4 flex items-center justify-between transition-all hover:bg-white/5"
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                  data-testid={`collection-row-${c.collection_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white">{c.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                      {lastViewedLabel ? (
                        <>
                          <Eye size={11} weight="fill" className="text-emerald-400/70" />
                          <span data-testid={`collection-last-view-${c.collection_id}`}>
                            {lastViewedLabel}
                          </span>
                        </>
                      ) : (
                        <span
                          className="text-gray-500"
                          data-testid={`collection-no-views-${c.collection_id}`}
                        >
                          — No views yet
                        </span>
                      )}
                    </div>
                  </div>
                  <ArrowRight size={16} weight="bold" className="text-gray-500" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspacePage;

/**
 * Best Moment This Week — single spotlight card. Premium feel: large hero
 * (thumb_800), soft shadow, minimal chrome. Selection is delegated to the
 * server (`/best-moment`); the card just renders. Copy line swaps based on
 * forward count: ≥5 shows the exact number, lower says "Loved by families
 * this week" (per the brief: never show weak numbers).
 */
const BestMomentCard = ({ moment, forwardCount, shareCode }) => {
  const heroUrl =
    getMediaUrl(moment, { size: 'thumb_800' }) || getMediaUrl(moment.storage_path, null);
  const fullUrl = getMediaUrl(moment.storage_path, null);
  const isVideo = moment.media_type === 'video';

  const impactCopy =
    forwardCount >= 5
      ? `Shared with ${forwardCount} ${forwardCount === 1 ? 'family' : 'families'}`
      : 'Loved by families this week';

  return (
    <div className="mb-6" data-testid="best-moment-card">
      <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-amber-300/80 mb-3 flex items-center gap-1.5">
        <Star size={12} weight="fill" className="text-amber-300" />
        Best moment this week
      </p>
      <div
        className="relative overflow-hidden rounded-2xl"
        style={{
          aspectRatio: '16 / 10',
          boxShadow: '0 20px 60px -20px rgba(0,0,0,0.55)',
          border: '1px solid rgba(255,255,255,0.06)',
          background: '#0F1014',
        }}
      >
        {isVideo ? (
          <video
            src={fullUrl}
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="best-moment-media"
          />
        ) : (
          <img
            src={heroUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="best-moment-media"
          />
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 55%)',
          }}
        />
        <div className="absolute bottom-0 left-0 right-0 px-5 pb-5 pt-12 text-white flex items-end justify-between gap-3">
          <p
            className="text-sm font-medium opacity-95"
            data-testid="best-moment-impact"
          >
            {impactCopy}
          </p>
          {shareCode && (
            <div className="flex items-center gap-2 whitespace-nowrap">
              {/* Secondary: forward this specific moment. mailto:, tagged
                  with ?u={upload_id} so per-photo attribution lands when
                  the recipient opens the link. */}
              <a
                href={(() => {
                  const url = `${window.location.origin}/c/${shareCode}/week?ref=forward&u=${encodeURIComponent(moment.upload_id)}`;
                  const subject = encodeURIComponent("A moment from this week");
                  const body = encodeURIComponent(`I thought you'd love this moment ❤️\n\n${url}`);
                  return `mailto:?subject=${subject}&body=${body}`;
                })()}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full text-white/90 hover:text-white hover:bg-white/10 transition-colors"
                style={{ border: '1px solid rgba(255,255,255,0.20)' }}
                data-testid="best-moment-forward-cta"
              >
                Forward
                <span aria-hidden>📤</span>
              </a>
              <a
                href={`/c/${shareCode}/week`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full bg-white text-black hover:bg-gray-100 transition-colors"
                data-testid="best-moment-cta"
              >
                View moment
                <ArrowRight size={12} weight="bold" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Most Shared Moment Ever — lifetime recognition. Same design language as
 * BestMoment for visual coherence, but slightly more prestige: 🏆 amber-300
 * eyebrow, gold-tinted shadow, subtle fade-in on first paint. Hidden until
 * the top upload has ≥3 forwards (server-enforced floor).
 *
 * Copy ladder per spec — protect against weak numbers:
 *   • ≥10 → "Reached N families ❤️"   (prestige tier)
 *   • 5-9 → "Shared with N families ❤️"
 *   • 3-4 → "Loved by families ❤️"    (warm fallback)
 */
const MostSharedMomentCard = ({ moment, forwardCount, shareCode }) => {
  const heroUrl =
    getMediaUrl(moment, { size: 'thumb_800' }) || getMediaUrl(moment.storage_path, null);
  const fullUrl = getMediaUrl(moment.storage_path, null);
  const isVideo = moment.media_type === 'video';

  let impactCopy;
  if (forwardCount >= 10) {
    impactCopy = `Reached ${forwardCount} families ❤️`;
  } else if (forwardCount >= 5) {
    impactCopy = `Shared with ${forwardCount} families ❤️`;
  } else {
    impactCopy = 'Loved by families ❤️';
  }

  return (
    <div
      className="mb-6 most-shared-card"
      data-testid="most-shared-card"
      style={{ animation: 'mostSharedFadeIn 600ms ease-out both' }}
    >
      <style>{`
        @keyframes mostSharedFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes reelPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(26, 24, 20, 0); }
          50%      { box-shadow: 0 0 0 10px rgba(26, 24, 20, 0.18); }
        }
        .reel-pulse { animation: reelPulse 1.5s ease-in-out 3 both; }
      `}</style>
      <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-amber-300/90 mb-3 flex items-center gap-1.5">
        <span aria-hidden>🏆</span>
        Your most shared moment
      </p>
      <div
        className="relative overflow-hidden rounded-2xl"
        style={{
          aspectRatio: '16 / 10',
          // slightly deeper / warmer shadow than Best Moment for prestige
          boxShadow: '0 28px 80px -20px rgba(180, 130, 20, 0.30), 0 16px 40px -16px rgba(0,0,0,0.55)',
          border: '1px solid rgba(252, 211, 77, 0.18)',
          background: '#0F1014',
        }}
      >
        {isVideo ? (
          <video
            src={fullUrl}
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="most-shared-media"
          />
        ) : (
          <img
            src={heroUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="most-shared-media"
          />
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(to top, rgba(0,0,0,0.70) 0%, rgba(0,0,0,0) 55%)',
          }}
        />
        <div className="absolute bottom-0 left-0 right-0 px-5 pb-5 pt-12 text-white flex items-end justify-between gap-3">
          <p
            className="text-sm font-medium opacity-95"
            data-testid="most-shared-impact"
          >
            {impactCopy}
          </p>
          {shareCode && (
            <a
              href={`/c/${shareCode}/week`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full bg-white text-black hover:bg-gray-100 transition-colors whitespace-nowrap"
              data-testid="most-shared-cta"
            >
              View moment
              <ArrowRight size={12} weight="bold" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * End-of-Term Highlights Reel — macro artefact. Premium editorial preview
 * matching the email layout. Hero (full-width) → 3-col supporting grid →
 * narrative line → "Send to families" CTA. Hidden until the backend reports
 * `ready: true`; once sent, the CTA flips to a calm "✓ Sent on …" state to
 * prevent accidental double-sends.
 */
const HighlightsReelCard = ({ picks, workspaceName, lastSentAt, sending, onSend, printLoading, onPrint, onCreateFilm }) => {
  const hero = picks[0];
  const supporting = picks.slice(1, 7);
  const sentDate = lastSentAt
    ? new Date(lastSentAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="mb-6" data-testid="highlights-reel-card">
      <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-300/90 mb-3 flex items-center gap-1.5">
        <span aria-hidden>✨</span>
        End of term, in pictures
      </p>
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: '#FFFFFF',
          color: '#1A1814',
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.55)',
          border: '1px solid rgba(255,255,255,0.04)',
        }}
      >
        {/* Hero */}
        <img
          src={getMediaUrl(hero, { size: 'thumb_800' })}
          alt=""
          className="block w-full"
          style={{ maxHeight: '420px', objectFit: 'cover' }}
          data-testid="highlights-reel-hero"
        />
        {/* Title */}
        <div className="px-7 pt-8 pb-2 text-center" style={{ fontFamily: 'Georgia, "Iowan Old Style", serif' }}>
          <p
            className="text-[10px] uppercase font-bold mb-3"
            style={{ letterSpacing: '0.28em', color: '#9A8B6B', fontFamily: 'Helvetica, Arial, sans-serif' }}
          >
            ✨ A Term in Pictures
          </p>
          <h3 className="text-2xl sm:text-3xl font-normal mb-1" style={{ letterSpacing: '-0.01em' }}>
            The moments that mattered
          </h3>
          <p className="text-sm italic" style={{ color: '#7A6F5C', marginTop: '12px' }}>
            from {workspaceName}.
          </p>
        </div>
        {/* Supporting grid */}
        {supporting.length > 0 && (
          <div className="px-5 pt-5 pb-2 grid grid-cols-3 gap-2">
            {supporting.map((p) => (
              <div
                key={p.upload_id}
                className="overflow-hidden"
                style={{ aspectRatio: '1 / 1', borderRadius: '8px', background: '#F2EBDB' }}
              >
                <img
                  src={getMediaUrl(p, { size: 'thumb_400' })}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}
        {/* Narrative + CTA */}
        <div className="px-7 pt-6 pb-8 text-center" style={{ fontFamily: 'Georgia, serif' }}>
          <p className="text-base italic mb-6" style={{ color: '#3A352E' }}>
            These were the moments families held onto ❤️
          </p>
          {sentDate ? (
            <p
              className="text-xs"
              style={{ color: '#9A8B6B', fontFamily: 'Helvetica, Arial, sans-serif', letterSpacing: '0.04em' }}
              data-testid="highlights-reel-sent-state"
            >
              ✓ Sent to families on {sentDate}
            </p>
          ) : (
            <button
              onClick={onSend}
              disabled={sending}
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.06em] px-7 py-3.5 rounded-full bg-black text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-wait transition-colors"
              style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
              data-testid="highlights-reel-send"
            >
              {sending ? 'Sending…' : 'Send to families →'}
            </button>
          )}
          {/* Print upsell — quiet text-link tier, well below the primary
              Send CTA so it never competes with the main action but stays
              visible at the moment of peak emotional weight. */}
          {onPrint && (
            <p style={{ marginTop: '18px' }}>
              <button
                onClick={onPrint}
                disabled={printLoading}
                className="text-xs underline-offset-2 hover:underline disabled:opacity-50 disabled:cursor-wait"
                style={{
                  color: '#7A6F5C',
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  letterSpacing: '0.02em',
                  background: 'none',
                  border: 'none',
                  cursor: printLoading ? 'wait' : 'pointer',
                  padding: 0,
                }}
                data-testid="highlights-reel-print"
              >
                {printLoading
                  ? 'Opening checkout…'
                  : 'Order printed memory book — £24.99 →'}
              </button>
            </p>
          )}
          {onCreateFilm && (
            <p style={{ marginTop: '12px' }}>
              <button
                onClick={onCreateFilm}
                className="text-xs underline-offset-2 hover:underline"
                style={{
                  color: '#7A6F5C',
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  letterSpacing: '0.02em',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
                data-testid="highlights-reel-film"
              >
                🎬 Turn this into a short film →
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Lean "momentum signal" strip — designed to feel emotionally rewarding for
 * the teacher (validation that their effort lands), NOT to be a dashboard.
 * Renders only when there's something positive to show.
 */
const EngagementStrip = ({ engagement, streakDays }) => {
  const {
    weekly_views,
    previous_week_views,
    active_collections,
    total_collections,
    forward_views_7d = 0,
  } = engagement;
  const delta = weekly_views - (previous_week_views || 0);
  const showStreak = streakDays >= 2;
  const streakCopy =
    streakDays >= 5
      ? `${streakDays} days of updates — parents are loving it`
      : `${streakDays} days of updates · Keep going`;
  // Share signal — quiet, only when ≥3 forwards in the rolling 7-day window
  // (per spec: never show "1 share"). 3–5 stays vague; 6+ shows the count.
  let shareCopy = null;
  if (forward_views_7d >= 6) {
    shareCopy = `Shared with ${forward_views_7d} families this week`;
  } else if (forward_views_7d >= 3) {
    shareCopy = 'Shared with a few families this week';
  }

  return (
    <div
      className="rounded-2xl p-5 mb-6"
      style={{
        background: 'linear-gradient(135deg, rgba(184,151,98,0.10), rgba(194,157,82,0.06))',
        border: '1px solid rgba(194,157,82,0.20)',
      }}
      data-testid="engagement-strip"
    >
      <div className="flex items-baseline gap-2 mb-1 flex-wrap">
        <Eye size={16} weight="fill" className="text-[#C29D52]" />
        <span className="text-2xl font-black text-white" data-testid="engagement-weekly-views">
          {weekly_views}
        </span>
        <span className="text-sm text-gray-300">
          parent {weekly_views === 1 ? 'view' : 'views'} this week
        </span>
        <span className="text-gray-500 mx-1">·</span>
        <span className="text-sm text-gray-300">
          <strong className="text-white">{active_collections}</strong> of {total_collections} active
        </span>
        <div className="ml-auto flex items-center gap-2">
          {showStreak && (
            <span
              className="text-xs font-bold flex items-center gap-1 text-amber-300 px-2.5 py-1 rounded-full"
              style={{
                background: 'rgba(245,158,11,0.10)',
                border: '1px solid rgba(245,158,11,0.30)',
              }}
              data-testid="streak-chip"
            >
              <Fire size={11} weight="fill" /> {streakCopy}
            </span>
          )}
          {delta > 0 && previous_week_views > 0 && (
            <span
              className="text-xs font-bold flex items-center gap-1 text-emerald-300"
              data-testid="engagement-delta"
            >
              <TrendUp size={11} weight="bold" /> +{delta} vs last week
            </span>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-1">
        <Heart size={11} weight="fill" className="text-[#B89762]/70" />
        You're keeping families connected.
      </p>
      {shareCopy && (
        <p
          className="text-xs text-gray-500 mt-1.5 flex items-center gap-1.5"
          data-testid="engagement-share-signal"
        >
          <span aria-hidden>📤</span>
          {shareCopy}
        </p>
      )}
    </div>
  );
};

/**
 * Smart Nudge — positive framing, action-oriented, dismissable. One only.
 * Drops the teacher onto the target collection's timeline with the upload
 * picker pre-opened (`?upload=1`).
 */
const NudgeBanner = ({ nudge, onAct, onDismiss }) => {
  const headline =
    nudge.reason === 'no_views'
      ? `${nudge.name} hasn't been seen yet`
      : `${nudge.name} hasn't been viewed in ${nudge.days_since_viewed} days`;
  return (
    <div
      className="rounded-2xl p-5 mb-6 relative"
      style={{
        background: 'linear-gradient(135deg, rgba(245,158,11,0.10), rgba(220,38,38,0.06))',
        border: '1px solid rgba(245,158,11,0.25)',
      }}
      data-testid="nudge-banner"
    >
      <button
        type="button"
        onClick={onDismiss}
        className="absolute top-3 right-3 text-gray-500 hover:text-white transition-colors"
        aria-label="Dismiss nudge"
        data-testid="nudge-dismiss"
      >
        <X size={14} weight="bold" />
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Megaphone size={14} weight="fill" className="text-amber-300" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300">
          Bring parents back today
        </span>
      </div>
      <h3 className="text-base font-bold text-white mb-0.5" data-testid="nudge-headline">
        {headline}
      </h3>
      <p className="text-sm text-gray-400 mb-4">
        Add a quick photo to get it active again.
      </p>
      <button
        onClick={onAct}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.015]"
        style={{ background: 'linear-gradient(135deg, #F59E0B, #DC2626)' }}
        data-testid="nudge-act"
      >
        <Plus size={12} weight="bold" /> Upload to {nudge.name}
      </button>
    </div>
  );
};
